import { Component, Vue } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import MiForm, { MiFormField } from '@/components/MiForm/MiForm';
import { Report } from '@/models/Report';
import { Question } from '@/models/Question';
import { Answer } from '@/models/Answer';
import Questionnaire from '@/components/Questionnaire/Questionnaire.vue';
import ErrorHandler from '@/support/ErrorHandler';
import DefaultDialog from '@/components/dialog/DefaultDialog.vue';
import OverwriteAssignmentValueDialog from '@/components/OverwriteAssignmentValueDialog/OverwriteAssignmentValueDialog.vue';
import OverwriteApplicantDialog from "@/components/OverwriteApplicantDialog/OverwriteApplicantDialog.vue";
import OverwriteIntermediaryDialog from "@/components/OverwriteIntermediaryDialog/OverwriteIntermediaryDialog.vue";
import DatePlanningBlock from '@/components/DatePlanningBlock/DatePlanningBlock.vue';
import { DateFormatter } from '@/support/DateFormatter';
import { DateTime } from 'luxon';
import { Contractor } from '@/models/Contractor';
import { Address, AddressPayload } from '@/models/Address';
import { Rpc } from '@/models/Rpc';
import { ReportStatus } from '@/support/ReportStatus';
import { AppointmentEnum } from '@/components/OverwriteAppointmentDialog/OverwriteAppointmentDialog';
import { OverwriteDateRequest, UpdateDateRequest } from '@/components/DatePlanningInput/DatePlanningInput';

@Component<Contract>({
  components: {
    Questionnaire,
    DefaultDialog,
    DatePlanningBlock,
    OverwriteAssignmentValueDialog,
    OverwriteApplicantDialog,
    OverwriteIntermediaryDialog,
  },
})
export default class Contract extends Vue {
  protected $pageTitle = 'Opdrachtverstrekking';

  protected questions: Question[] = [];

  protected answers: Answer[] = [];

  protected formFields: MiFormField[] = [];

  protected isFormValid = true;

  protected report: Report | null = null;

  protected defaultPdf: Record<string, string> = {};

  protected isLoadingPdf = true;

  protected isLoadingForm = true;

  protected isFinalizingContract = false;

  protected isUpdating = false;

  protected firstAppointment: DateFormatter = new DateFormatter();

  protected isEditingFirstAppointment = false;

  protected isEditingDeliveryAppointment = false;

  protected instructions = '';

  protected allContractors: Contractor[] = [];

  protected selectedContractor = '';

  protected addressError: string | null = null;

  protected overwriteAssignmentValue = false;

  protected overwriteApplicant = false;

  protected overwriteIntermediary = false;

  protected AppointmentEnum = AppointmentEnum;

  protected reportIncludes = ['answers', 'contractor', 'applicant', 'intermediary'];

  protected async mounted(): Promise<void> {
    await this.initialize();
    await this.fetchAllContractors();
    const pdf = await this.report?.getContract();
    this.defaultPdf = pdf;
    this.isLoadingPdf = false;
  }

  protected async initialize(): Promise<void> {
    this.isLoadingForm = true;
    await this.fetchReport();
    await this.fetchQuestions();
    this.buildForm();
    this.selectedContractor = this.report && this.report.contractor ? `${this.report?.contractor?.uuid}` : '';

    if (this.firstAppointmentIsScheduled) {
      this.firstAppointment.selectedDate = DateTime.fromSQL(`${this.report?.first_appointment}`).toFormat('yyyy-MM-dd');
      this.firstAppointment.selectedTime = DateTime.fromSQL(`${this.report?.first_appointment}`).toFormat('HH:mm:ss');
    }
    this.isLoadingForm = false;
  }

  protected handleBesluitBestuurUpdated(date: string): void {
    const firstAppointmentDate = DateTime.fromISO(date).plus({ days: 42 });
    this.firstAppointment.selectedDate = firstAppointmentDate.toFormat('yyyy-MM-dd');
    this.firstAppointment.selectedTime = '';
    this.updateReport({ first_appointment: this.firstAppointment.selectedDate });
  }

  protected async handleAssigmentValueChanged(): Promise<void> {
    await this.initialize();
    const pdf = await this.report?.getContract();
    this.defaultPdf = pdf;
  }

  protected async handleApplicantChanged(): Promise<void> {
    this.overwriteApplicant = false;
    await this.initialize();
  }

  protected async handleIntermediaryChanged(): Promise<void> {
    this.overwriteIntermediary = false;
    await this.initialize();
  }

  protected async refreshContract(): Promise<void> {
    this.isLoadingPdf = true;
    const pdf = await this.report?.getContract();
    this.defaultPdf = pdf;
    this.isLoadingPdf = false;
  }

  protected async fetchAllContractors(): Promise<void> {
    await new Contractor().limit(1000).all()
      .then((contractors: Contractor[]) => {
        this.allContractors = contractors;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async fetchReport(): Promise<void> {
    if (! this.$route.params.id) {
      return;
    }

    this.report = null;

    this.report = await new Report()
      .include(this.reportIncludes)
      .find(this.$route.params.id)
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });

    if (! this.report) {
      return;
    }

    if (! this.report.answers) {
      return;
    }

    this.answers = this.report.answers;
  }

  protected async fetchQuestions(): Promise<void> {
    let questions = await new Question().filter({ section: 'contract' }).all()
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });

    questions = questions.map((question: any) => (['agreement_date', 'agreement_number'].includes(question.key)
      ? {
        ...question,
        is_disabled: true,
      }
      : question));

    this.questions = questions;
  }

  protected async fetchAddress(): Promise<void> {
    const data = await this.addressForm.parseFromData();
    this.setAddress({ street: '', city: '' } as Address);

    if (! data || ! data.postcode || ! data.number) { return; }
    await this.rpcPost('address:suggest', {
      postcode: data.postcode ? data.postcode : '',
      number: data.number ? data.number : '',
      addition: data.number_add && data.number_add.length ? data.number_add : '',
    })
      .then(async (address: Address) => {
        this.addressError = null;

        console.log('fetchAddress', address.getPayload);
        this.setAddress(address);

        if (! address) {
          return;
        }

        this.saveAddress(address);
      })
      .catch((error: AxiosError) => {
        this.setAddressError(error.message);
      });
  }

  protected getAddressPayload(address: Address): AddressPayload {
    return {
      street: address.street,
      number: address.number,
      number_add: address.number_add,
      city: address.city,
      municipality: address.municipality,
      postcode: address.postcode,
      country: address.country,
    } as AddressPayload;
  }

  protected async saveAddress(address: Address): Promise<void> {
    const payload = { address: this.getAddressPayload(address) };

    await this.report?.update(payload)
    .then(() => {
      this.refreshContract();
    })
    .catch((error: AxiosError) => {
      this.setAddressError(error.message);
    });
  }

  protected setAddress(address: Address): void {
    this.addressForm.setValue('street', address && address.street ? address.street : '');
    this.addressForm.setValue('city', address && address.city ? address.city : '');
  }

  protected setAddressError(message: string): void {
    let errorMessage = 'Dit adres is niet gevonden, controleer de adresgegevens.';
    if (message === 'addition not found') {
      errorMessage = 'Deze toevoeging is niet gevonden, controleer de adresgegevens';
    } else if (message === 'Streetnumber not found') {
      errorMessage = 'Dit huisnummer is niet gevonden, controleer de adresgegevens';
    } else if (message === 'post code not found') {
      errorMessage = 'Deze postcode is niet gevonden, controleer de adresgegevens';
    }

    this.addressError = errorMessage;
  }

  protected async rpcPost(signature: string, body: Record<string, string | string[]>): Promise<any> {
    const payload = {
      signature,
      body,
    };

    const rpcResponse = await new Rpc()
      .post(payload);

    return rpcResponse;
  }

  protected async handleAddressChanged(field: MiFormField): Promise<void> {
    if (field.key === 'city' || field.key === 'street') {
      return;
    }
    this.fetchAddress();
  }

  protected handleFieldChanged(field: MiFormField): void {
    if (! field.value) {
      return;
    }

    const question = this.questions.find((questionEntry: Question) => questionEntry.key === field.key);

    if (! question) {
      return;
    }

    const payload = {
      hin_report: this.$route.params.id,
      question: question.uuid,
      value: field.value,
    };

    new Answer().create(payload);
  }

  protected close(): void {
    this.instructions = '';
    this.isFinalizingContract = false;
  }

  protected async updateReport(payload: Record<string, any>): Promise<void> {
    if (! this.report) {
      return;
    }
    await new Report(this.report).update(
      payload,
    ).then(() => {
      this.initialize();
    })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async onUpdateDate(request: UpdateDateRequest): Promise<void> {
    const payload: Record<string, string> = {};

    payload[request.field] = `${request.date}`;
    await this.updateReport(payload);
  }

  protected async onOverwriteDate(request: OverwriteDateRequest): Promise<void> {
    const payload: Record<string, string> = {};

    payload[request.field] = `${request.event.date}`;
    payload[`${request.field}_change_reason`] = `${request.event.reason}`;

    await this.updateReport(payload);
  }

  protected async finalize(): Promise<void> {
    if (! this.report) {
      return;
    }

    const payload = {
      status: ReportStatus.WAITING_FOR_SIGNATURE,
      instructions: this.instructions,
    };

    this.isUpdating = true;

    await this.report
      .update(payload)
      .then(() => {
        this.$router.push(`/reports/${this.report?.uuid}`);
      })
      .catch((error: AxiosError) => {
        this.isUpdating = false;
        ErrorHandler.network(error);
      });
  }

  protected async updateReportContractor(): Promise<void> {
    if (! this.report) {
      return;
    }

    const contractor = this.selectedContractor;

    this.report = await new Report(this.report)
      .include(this.reportIncludes)
      .update({
        contractor,
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });

    if (! this.report) {
      return;
    }

    if (! this.report.answers) {
      return;
    }

    this.answers = this.report.answers;
  }

  protected async updateReportStatus(status: string): Promise<void> {
    if (! this.report) {
      return;
    }

    await new Report(this.report).update({ status });
  }

  protected buildForm(): void {
    const fields: MiFormField[] = [];
    const len = this.questions.length;

    for (let i = 0; i < len; i += 1) {
      fields.push({
        type: this.questions[i].type_raw !== undefined ? `${this.questions[i].type_raw}` : 'text',
        key: this.questions[i].key !== undefined ? `${this.questions[i].key}` : '',
        placeholder: this.questions[i].placeholder,
        label: this.questions[i].label,
        rules: this.questions[i].is_required ? ['required'] : [],
      });
    }

    this.formFields = fields;
  }

  protected get deliveryDate(): string {
    if (! this.report) {
      return '';
    }
    const date = this.report?.delivery_appointment?.split(' ');
    return date ? date[0] : '';
  }

  protected get firstAppointmentDate(): string {
    if (! this.report) {
      return '';
    }
    const date = this.report?.first_appointment?.split(' ');
    return date ? date[0] : '';
  }

  protected get addressFields(): MiFormField[] {
    return [
      {
        type: 'text',
        key: 'postcode',
        placeholder: 'Postcode',
        label: 'Postcode',
        rules: ['required', 'dutchPostcode'],
        class: 'w-1/2',
        value: this.report && this.report.address && this.report.address.postcode ? this.report.address.postcode : '',
      },
      {
        type: 'number',
        key: 'number',
        placeholder: 'Huisnummer',
        label: 'Huisnummer',
        rules: ['required'],
        class: 'w-1/4 pl-32',
        value: this.report && this.report.address && this.report.address.number ? `${this.report.address.number}` : '',
      },
      {
        type: 'text',
        key: 'number_add',
        placeholder: 'Toevoeging',
        label: 'Toevoeging',
        class: 'w-1/4 pl-32',
        value: this.report && this.report.address && this.report.address.number_add ? this.report.address.number_add : '',
      },
      {
        type: 'text',
        key: 'street',
        placeholder: 'Straat',
        label: 'Straat',
        rules: ['required'],
        disabled: true,
        hideDetails: true,
        value: this.report && this.report.address && this.report.address.street ? this.report.address.street : '',
      },
      {
        type: 'text',
        key: 'city',
        placeholder: 'Stad',
        label: 'Stad',
        rules: ['required'],
        disabled: true,
        hideDetails: true,
        value: this.report && this.report.address && this.report.address.city ? this.report.address.city : '',
      },
    ];
  }

  protected get firstAppointmentIsScheduled(): boolean {
    return this.report?.first_appointment !== '00-00-0000 00:00';
  }

  protected get OriginalFirstAppointmentIsScheduled(): boolean {
    return this.report?.original_first_appointment !== '00-00-0000 00:00';
  }

  protected get deliveryIsScheduled(): boolean {
    return this.report?.delivery_appointment !== '00-00-0000 00:00';
  }

  protected get addressForm(): MiForm {
    return this.$refs.addressForm as MiForm;
  }

  protected get reportHasNonEditableStatus(): boolean {
    if (! this.report) {
      return false;
    }
    return ['hin_aborted', 'hin_closed'].includes(this.report.status_raw ? this.report.status_raw : '');
  }
}
